/**
 * StrategySummary.jsx
 * The month's objective, frequency, journey and content mix.
 *
 * Everything on this card is the model's proposal, not a measurement, and the
 * card says so rather than letting a confident-looking percentage pass for a
 * finding. The mix bar carries its own labels because the palette's lighter
 * steps do not clear 3:1 against a white card — the figures are never colour
 * alone.
 *
 * Dependencies: components/Icons.jsx
 */

import { Calendar, Chart, Sparkle, Target, Users } from '../Icons.jsx';

/** Five slots, matching the mix palette in copilot.css. */
const SLOTS = 5;

export default function StrategySummary({ strategy, monthLabel, generating, onGenerate }) {
  if (!strategy) {
    return (
      <section className="card">
        <div className="card__head">
          <span className="tile">
            <Chart size={21} />
          </span>
          <div className="card__head-text">
            <h2>Strategy summary</h2>
            <p>Nothing planned for {monthLabel} yet.</p>
          </div>
        </div>

        <div className="empty empty--inline">
          <div className="empty__art">
            <Sparkle size={26} />
          </div>
          <h3>No strategy for {monthLabel}</h3>
          <p>
            Copilot reads the brand brief and proposes an objective, a content mix and the posts
            to get there. Nothing is written or scheduled at this step.
          </p>
          <button className="btn btn--primary" onClick={onGenerate} disabled={generating}>
            <Sparkle size={16} /> {generating ? 'Thinking…' : `Generate ${monthLabel} strategy`}
          </button>
        </div>
      </section>
    );
  }

  const mix = strategy.mix ?? [];

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Chart size={21} />
        </span>
        <div className="card__head-text">
          <h2>Strategy summary</h2>
          <p>Proposed by Copilot for {monthLabel}.</p>
        </div>
        <div className="card__head-end">
          <span className={`badge ${strategy.generatedAt ? 'badge--ok' : 'badge--accent'}`}>
            <span className="dot" />
            {strategy.generatedAt ? 'Month generated' : 'Ready'}
          </span>
        </div>
      </div>

      <div className="goal-block">
        <span className="tile tile--sm">
          <Target size={18} />
        </span>
        <div>
          <div className="goal-block__eyebrow">30-day goal</div>
          <h3>{strategy.goal || 'No objective given'}</h3>
          {strategy.summary && <p>{strategy.summary}</p>}
        </div>
      </div>

      <div className="facts">
        <div className="fact">
          <span className="tile tile--sm">
            <Calendar size={17} />
          </span>
          <div>
            <div className="fact__label">Recommended frequency</div>
            <div className="fact__value">
              {strategy.perWeekMin === strategy.perWeekMax
                ? `${strategy.perWeekMin} posts per week`
                : `${strategy.perWeekMin}–${strategy.perWeekMax} posts per week`}
            </div>
          </div>
        </div>

        <div className="fact">
          <span className="tile tile--sm">
            <Sparkle size={17} />
          </span>
          <div>
            <div className="fact__label">Campaign focus</div>
            <div className="fact__value">{strategy.funnel || '—'}</div>
          </div>
        </div>

        <div className="fact">
          <span className="tile tile--sm">
            <Users size={17} />
          </span>
          <div>
            <div className="fact__label">Target audience</div>
            <div className="fact__value">{strategy.audience || '—'}</div>
          </div>
        </div>
      </div>

      {mix.length > 0 && (
        <div className="mix">
          <div className="mix__title">Recommended content mix</div>

          {/* A 2px surface gap between segments, so adjacent pillars stay
              separable for a reader who cannot tell the hues apart. */}
          <div
            className="mix__bar"
            role="img"
            aria-label={mix.map((m) => `${m.pillar} ${m.percent}%`).join(', ')}
          >
            {mix.map((m, i) => (
              <span
                key={m.pillar}
                className={`mix__seg mix__seg--${(i % SLOTS) + 1}`}
                style={{ flexGrow: m.percent }}
                title={`${m.pillar} — ${m.percent}%`}
              />
            ))}
          </div>

          <ul className="mix__legend">
            {mix.map((m, i) => (
              <li key={m.pillar} title={m.description || undefined}>
                <span className={`mix__key mix__key--${(i % SLOTS) + 1}`} />
                <span className="mix__pillar">{m.pillar}</span>
                <strong>{m.percent}%</strong>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Stated plainly. A percentage that looks like this is easy to mistake
          for something that was measured. */}
      <p className="card__footnote">
        A proposal, not a measurement. Copilot wrote this from the brand brief — nothing here
        comes from your own figures. What actually happened is on the Analytics screen.
      </p>
    </section>
  );
}
